import { useCallback, type VoidFunctionComponent } from 'react';

import { getGitServiceName } from '../../../../../shared/projectsV2/vcs';
import { useOnConfirm } from '../../../../contexts/DialogContext';
import { getAuthorizeServiceURL } from '../../../../helpers/services';
import { hardRedirectTo } from '../../../../utils';
import Button from '../../../generic/Button';
import { DocsLink } from '../../../generic/Link';
import Redirect from '../../../generic/Redirect';
import Header from '../../../shared/settings/SettingsPageHeader';
import Paragraph from '../../../shared/settings/SettingsPageParagraph';
import Subheading from '../../../shared/settings/SettingsPageSubheading';
import { useProjectSettingsContext } from '../context';
import PageContent from '../shared/SettingsPageContent';

import styles from './ProjectBuildsSettings.module.less';

/**
 * ProjectBuildsSettingsContainer
 */
const ProjectBuildsSettingsContainer: VoidFunctionComponent = () => {
  const { builds } = useProjectSettingsContext();
  const gitServiceName = getGitServiceName(builds?.vcs_host);
  const onConfirm = useOnConfirm();
  //  Resetting the credentials is a matter of navigating to the authorize endpoint with the project_id and private_access query parameters,
  //  which will prompt the user to re-authorize their account with the VCS provider and then redirect back to FOSSA.
  const onResetCredentials = useCallback(() =>
    hardRedirectTo(
      getAuthorizeServiceURL({
        vcsHost: builds?.vcs_host,
        location: window.location.href,
        projectLocator: builds.locator,
        isPrivateAccess: true,
      }),
    ), [builds.locator, builds?.vcs_host]);

  if (!builds.canUserEditSettings) {
    return (
      <Redirect to={builds.projectSettingsBaseURL} />
    );
  }

  return (
    <PageContent>
      <Header heading="Builds" />
      <Subheading>
        Provided Builds
      </Subheading>
      <Paragraph size="medium">
        To increase accuracy and performance, FOSSA allows you to provide build data from an existing environment
        {' '}
        (i.e. CI/CD machine) by installing a plugin. This is a great fit if you want to take advantage of a large
        {' '}
        or complex build that you&apos;ve already configured.
      </Paragraph>
      <Paragraph size="medium">
        Learn more about getting started with provided builds
        {' '}
        <DocsLink to="provided-builds">
          here
        </DocsLink>
        .
      </Paragraph>
      <Subheading>
        Automated Builds
      </Subheading>
      <Paragraph size="medium">
        By default, FOSSA will automatically analyze and build your code with the options specified here. This
        {' '}
        is a best fit for small projects or simple builds.
      </Paragraph>
      {builds?.vcs_host && (
        <div>
          <Subheading>
            Reset {gitServiceName} Credentials
          </Subheading>
          <p className={styles.text}>
            Reset {gitServiceName} login credentials for this project.
          </p>
          <Button
            size="large"
            type="error"
            onClick={() => onConfirm({
              title: `Reset ${gitServiceName} Credentials`,
              body: (
                <>
                  <p>This will replace the current saved credentials for this { gitServiceName } project</p>
                  <p className="text-center">
                    <b>Are you sure you want to do this?</b>
                  </p>
                </>
              ),
              onConfirm: onResetCredentials,
              confirmText: 'Reset Credentials',
              confirmBtnType: 'error',
            })}
          >
            Reset {gitServiceName} Credentials
          </Button>
        </div>
      )}
    </PageContent>
  );
};

export default ProjectBuildsSettingsContainer;
